/**
 * ClassesList - Editorial row layout for public class listings
 */

"use client";

import { Box, Flex, Icon } from "@chakra-ui/react";
import { HiOutlineChevronRight } from "react-icons/hi2";
import type { GetClassResponse } from "@/openapi/generated";
import { formatTimeTo12Hour } from "@/lib/utils";
import { getClassAvailabilityDisplay } from "@/lib/utils/classAvailability";
import { resolveClassPublicPath } from "@/lib/utils/publicPaths";
import { useThemeTokens } from "@/lib/theme/tokens";
import { Link, Text } from "@/components/ui";
import { ClassAvailabilityBadge } from "./ClassAvailabilityBadge";
import { ClassRegisterCta } from "./ClassRegisterCta";

export interface ClassesListProps {
  data: GetClassResponse[];
  /** Defaults to true. Homepage sections already draw the top rule. */
  showTopBorder?: boolean;
  /** Listing pages use h2 under the page hero; homepage selected classes sit under an h2. */
  titleAs?: "h2" | "h3";
}

interface ClassListItemProps {
  class: GetClassResponse;
  href: string;
  isLast: boolean;
  titleAs: "h2" | "h3";
}

const ClassListItem = ({
  class: classData,
  href,
  isLast,
  titleAs,
}: ClassListItemProps) => {
  const theme = useThemeTokens();

  const firstDay = classData.classDays?.[0];
  const timeText = firstDay
    ? `${formatTimeTo12Hour(firstDay.startTime)} – ${formatTimeTo12Hour(firstDay.endTime)}`
    : "Time TBD";
  const dayLabel = firstDay?.day ?? "Schedule TBD";

  const additionalDays =
    classData.classDays && classData.classDays.length > 1
      ? `+${classData.classDays.length - 1} more day${classData.classDays.length - 1 > 1 ? "s" : ""}`
      : null;

  const locationName = classData.location?.name;
  const roomName = classData.classRoom?.name;
  const metaParts = [locationName, roomName].filter(Boolean);
  const availability = getClassAvailabilityDisplay(classData);

  return (
    <Box
      as="li"
      listStyleType="none"
      position="relative"
      py={{ base: 5, md: 6 }}
      borderBottomWidth={isLast ? 0 : "1px"}
      borderColor={theme.borderGlass.subtle}
      transition="border-color 0.2s ease"
      sx={{
        "@media (hover: hover)": {
          "&:hover [data-class-title]": {
            color: theme.accent.primary,
          },
        },
      }}
    >
      <Link
        href={href}
        position="absolute"
        inset={0}
        display="block"
        zIndex={1}
        borderRadius="8px"
        aria-label={`View details for ${classData.name}`}
        _hover={{ textDecoration: "none" }}
        _focusVisible={{
          boxShadow: theme.focusRing,
        }}
      />

      <Flex
        direction={{ base: "column", lg: "row" }}
        align={{ base: "stretch", lg: "flex-start" }}
        gap={{ base: 4, lg: 8 }}
      >
        <Box flexShrink={0} w={{ base: "100%", lg: "148px" }}>
          <Text
            fontSize="xs"
            fontWeight="600"
            letterSpacing="0.14em"
            textTransform="uppercase"
            color={theme.text.tertiary}
            mb={1}
          >
            {dayLabel}
          </Text>
          <Text
            fontSize="sm"
            fontWeight="600"
            color={theme.text.primary}
            lineHeight="1.4"
          >
            {timeText}
          </Text>
          {additionalDays ? (
            <Text variant="tertiary" fontSize="xs" mt={1.5}>
              {additionalDays}
            </Text>
          ) : null}
          {availability ? (
            <Box mt={2.5}>
              <ClassAvailabilityBadge availability={availability} />
            </Box>
          ) : null}
        </Box>

        <Flex direction="column" flex={1} minW={0} gap={2}>
          <Text
            as={titleAs}
            data-class-title
            variant="primary"
            fontSize={{ base: "lg", md: "xl" }}
            fontWeight="600"
            letterSpacing="-0.02em"
            lineHeight="1.25"
            transition="color 0.2s ease"
            noOfLines={2}
          >
            {classData.name}
          </Text>

          {classData.description ? (
            <Text
              variant="secondary"
              fontSize="sm"
              lineHeight="1.6"
              noOfLines={{ base: 3, md: 2 }}
            >
              {classData.description}
            </Text>
          ) : null}

          {metaParts.length > 0 ? (
            <Text variant="tertiary" fontSize="sm" lineHeight="1.5">
              {metaParts.join(" · ")}
            </Text>
          ) : null}
        </Flex>

        <Flex
          position="relative"
          zIndex={2}
          flexShrink={0}
          direction={{ base: "row", lg: "column" }}
          align={{ base: "center", lg: "flex-end" }}
          justify={{ base: "space-between", lg: "flex-start" }}
          gap={3}
          w={{ base: "100%", lg: "auto" }}
          minW={{ lg: "148px" }}
          pt={{ lg: 1 }}
        >
          <ClassRegisterCta
            classId={Number(classData.classId)}
            allowOnlineRegistration={classData.allowOnlineRegistration}
            variant="compact"
          />
          <Flex
            align="center"
            gap={1}
            color={theme.text.tertiary}
            fontSize="sm"
            fontWeight="500"
            whiteSpace="nowrap"
            pointerEvents="none"
          >
            <Text as="span">Details</Text>
            <Icon
              as={HiOutlineChevronRight}
              boxSize="16px"
              aria-hidden
            />
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
};

export const ClassesList = ({
  data,
  showTopBorder = true,
  titleAs = "h2",
}: ClassesListProps) => {
  const theme = useThemeTokens();

  return (
    <Box
      as="ul"
      listStyleType="none"
      m={0}
      p={0}
      borderTopWidth={showTopBorder ? "1px" : 0}
      borderColor={theme.borderGlass.subtle}
    >
      {data.map((classItem, index) => (
        <ClassListItem
          key={String(classItem.classId)}
          class={classItem}
          isLast={index === data.length - 1}
          titleAs={titleAs}
          href={resolveClassPublicPath({
            name: classItem.name || "class",
            classId: Number(classItem.classId),
            slug: classItem.slug,
          })}
        />
      ))}
    </Box>
  );
};
